import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randPastDate,
  randRecentDate,
  randNumber,
  randChanceBoolean,
  randProductName,
} from '@ngneat/falso'

import type { SchemaAttributes } from '../../types.js'
import type { MockServer } from '../server.js'
import { getExistingEntity } from '../utils.js'

type ConsultationMedicine = SchemaAttributes<'ConsultationMedicine'>

export const model = (Model as ModelDefinition<ConsultationMedicine>).extend({
  stockLocation: belongsTo('stockLocation'),
  batch: belongsTo('batch'),
  createdUser: belongsTo('user'),
  modifiedUser: belongsTo('user'),
  supervisingVeterinarian: belongsTo('user'),
  performedBy: belongsTo('user'),
  patient: belongsTo('patient'),
})

export const factory = Factory.extend<ConsultationMedicine>({
  code() {
    return 'MED-'.concat(String(randNumber({ min: 1000, max: 9999 })))
  },
  name() {
    return randProductName()
  },
  usage_type() {
    return rand(['ADMINISTERED', 'PRESCRIPTION', 'GIVEN', 'HOME_DELIVERY'])
  },
  used() {
    return randChanceBoolean({ chanceTrue: 0.8 })
      ? randRecentDate().toISOString()
      : null
  },
  created() {
    return randPastDate().toISOString()
  },
  modified() {
    return randRecentDate().toISOString()
  },
  herd_size_multiplier() {
    return randChanceBoolean({ chanceTrue: 0.3 })
      ? randNumber({ min: 1, max: 10 })
      : null
  },
  display_price() {
    return randNumber({ min: 10, max: 500, precision: 2 })
  },
  hidden_items_display_price() {
    return randChanceBoolean({ chanceTrue: 0.2 })
      ? randNumber({ min: 5, max: 50, precision: 2 })
      : 0
  },
  is_minimum_charge_active() {
    return randChanceBoolean({ chanceTrue: 0.3 })
  },
  is_cabinet_item() {
    return randChanceBoolean({ chanceTrue: 0.5 })
  },
  display_amount() {
    return randNumber({ min: 1, max: 10, precision: 1 })
  },
  display_size() {
    const sizes = ['mg', 'box', 'bottle', 'ml']
    return rand(sizes)
  },
  instructions() {
    return randChanceBoolean({ chanceTrue: 0.7 })
      ? 'Administer once daily'
      : null
  },
  active_substance() {
    const substances = [
      'Amoxicillin',
      'Meloxicam',
      'Prednisolone',
      'Metronidazole',
      'Cephalexin',
    ]
    return randChanceBoolean({ chanceTrue: 0.8 }) ? rand(substances) : null
  },
  withdrawal_periods() {
    return randChanceBoolean({ chanceTrue: 0.6 })
      ? `Meat: ${randNumber({ min: 1, max: 30 })} days, Milk: ${randNumber({ min: 1, max: 15 })} days`
      : null
  },
  display_measurement_loss() {
    return randChanceBoolean({ chanceTrue: 0.5 })
      ? randNumber({ min: 1, max: 10, precision: 2 })
      : undefined
  },
  not_for_food() {
    return randChanceBoolean({ chanceTrue: 0.3 })
  },
  controlled_substance() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  administration_method() {
    return rand(['IM', 'IV', 'PO', 'SC', 'TOPICAL', null])
  },
  vaccination_disease() {
    return null
  },
  vaccination_type() {
    return null
  },

  afterCreate(medicine, server) {
    if (!medicine.stockLocation) {
      const stockLocation = server.schema.first('stockLocation')
      if (stockLocation) {
        medicine.update({ stockLocation })
      }
    }

    if (!medicine.batch && randChanceBoolean({ chanceTrue: 0.8 })) {
      const batch = server.create('batch')
      medicine.update({ batch })
    }

    if (!medicine.createdUser) {
      const createdUser = getExistingEntity(server, 'user')
      medicine.update({ createdUser })
    }

    if (!medicine.modifiedUser) {
      const modifiedUser = getExistingEntity(server, 'user')
      medicine.update({ modifiedUser })
    }
  },
})

export function routes(server: MockServer) {
  server.get('/consultation_medicines/:id', (schema, request) => {
    const { id } = request.params
    return schema.find('consultationMedicine', id)
  })
}
